import { z } from 'zod';
/**
 * The locale rails (docs/i18n-design.md).
 *
 * The document splits strings into three domains and this file serves the
 * second and third of them:
 *
 *  * **Domain B — tenant content** (item names, descriptions, `details[]`, menu
 *    categories, policies): *data*, not UI strings, stored as JSONB maps
 *    `{"en": "…", "es": "…"}` with **default-locale fallback**.
 *  * **Domain C — text we generate** (the availability `summary`): locale-aware
 *    generators, which need the same negotiation vocabulary.
 *
 * There is exactly **one** fallback function — `localize` — for the same reason
 * there is exactly one scheduling engine: a second one would eventually answer
 * differently, and the two answers would be a guest reading one name on the
 * booking site and hearing another from the agent. Every projection in
 * `packages/store` resolves through it.
 */
/* ------------------------------------------------------------------ */
/* The scalars                                                         */
/* ------------------------------------------------------------------ */
/**
 * A BCP-47 language tag, validated **loosely and on purpose**.
 *
 * `en`, `en-US`, `es`, `es-419`, `fr-HT`, `zh-Hant-TW` all pass. What is
 * rejected is the shape that would break a lookup rather than the tag that is
 * merely unusual: an empty string, whitespace, a bare region, punctuation, or
 * something long enough to be a sentence.
 *
 * Strict IANA registry validation is deliberately *not* done here. The registry
 * moves, a tenant may legitimately want a tag we have never seen (`aig` for
 * Antiguan creole, say), and a validator that refuses a real language because
 * our copy of the registry is eighteen months old is a validator that makes the
 * platform less useful than a plain text column. The cost of accepting an
 * invented tag is a translation nobody reads; the cost of refusing a real one is
 * a tenant who cannot describe their own menu.
 */
export const Locale = z
    .string()
    .min(2)
    .max(35)
    .regex(/^[A-Za-z]{2,8}(?:-[A-Za-z0-9]{1,8})*$/, 'must be a BCP-47 language tag');
/**
 * The locale everything falls back to when a tenant has declared nothing.
 *
 * English, because English is the USVI's official language and because every row
 * that existed before migration 0012 was written in it — the backfill keys on
 * the tenant's declared default and on this when there is none.
 */
export const DEFAULT_LOCALE = 'en';
/**
 * A piece of tenant content in every language it has been written in.
 *
 * Values are non-empty: an empty string is not a translation, it is an absence,
 * and the design is explicit that a missing translation falls back to the
 * tenant's default language **never to an empty string**. Storing `""` would
 * make the map lie about what it has.
 *
 * An empty map is legal and means "nothing written yet" — `localize` answers
 * `''` for it, which is the honest answer and the only one it ever gives.
 */
export const LocalizedText = z.record(Locale, z.string().min(1));
/**
 * The list-valued sibling — `item_scheduling.details`, the free-text operational
 * notes the agent may read aloud (obligation S4). A list per locale rather than
 * a locale per list item: the notes are one document in one voice, and pairing
 * them up index-by-index across languages would break the first time a tenant
 * writes three notes in English and two in Spanish.
 */
export const LocalizedList = z.record(Locale, z.array(z.string()));
/* ------------------------------------------------------------------ */
/* Matching                                                            */
/* ------------------------------------------------------------------ */
/** `es-MX` → `es`. Lower-cased, because tag comparison is case-insensitive. */
export function primarySubtag(locale) {
    const tag = locale.trim().toLowerCase();
    const dash = tag.indexOf('-');
    return dash === -1 ? tag : tag.slice(0, dash);
}
function canonical(locale) {
    return locale.trim().toLowerCase();
}
/**
 * The rungs `localize` walks, as a list of candidate keys for a map.
 *
 * Exposed because `negotiateLocale` walks the same ladder against a tenant's
 * declared `supported_locales`, and two ladders would be the beginning of two
 * fallback rules.
 */
export function localeCandidates(keys, locale) {
    const wanted = canonical(locale);
    if (wanted === '')
        return [];
    const primary = primarySubtag(wanted);
    const sorted = [...keys].sort();
    const exact = sorted.filter((k) => canonical(k) === wanted);
    const primaryExact = sorted.filter((k) => canonical(k) === primary && !exact.includes(k));
    // `es` asked for, `es-MX` stored: a regional variant of the right language is
    // a far better answer than the tenant's default, and a guest reading Mexican
    // Spanish instead of Castilian is not a bug worth an English fallback.
    const sameLanguage = sorted.filter((k) => primarySubtag(k) === primary && !exact.includes(k) && !primaryExact.includes(k));
    return [...exact, ...primaryExact, ...sameLanguage];
}
/* ------------------------------------------------------------------ */
/* The one fallback function                                           */
/* ------------------------------------------------------------------ */
function pick(map, locale, usable) {
    if (locale === null || locale === undefined)
        return undefined;
    for (const key of localeCandidates(Object.keys(map), locale)) {
        const value = map[key];
        if (value !== undefined && usable(value))
            return value;
    }
    return undefined;
}
function anyValue(map, usable) {
    for (const key of Object.keys(map).sort()) {
        const value = map[key];
        if (value !== undefined && usable(value))
            return value;
    }
    return undefined;
}
/**
 * Resolve a localized map to the one string a caller gets.
 *
 * The ladder, in order — **requested → primary-subtag match → tenant default →
 * any value**, and never `''` while the map holds anything at all:
 *
 *  1. the requested tag exactly (`es-MX`);
 *  2. its primary subtag (`es`);
 *  3. any stored tag in the same language (`es-419`);
 *  4-6. the same three rungs for the tenant's default locale;
 *  7. the alphabetically first non-empty value, so an answer is deterministic
 *     rather than dependent on JSONB key order;
 *  8. `''`, and only for a map with nothing usable in it.
 *
 * Rung 7 is the one worth defending. A tenant whose default locale was changed
 * after their catalog was written has rows keyed under neither the request nor
 * the new default, and the choice there is between the name they wrote and
 * nothing. A blank service name on a booking page is a worse failure than a
 * service name in the wrong language, so the map wins over the configuration.
 *
 * A bare string is accepted and returned as-is. Not defensive clutter: it is the
 * shape a row written before migration 0012 would have had, and the shape a hand
 * edit against the JSONB column can still produce (`'"Massage"'::jsonb` is a
 * perfectly good JSON document). Answering it is one line; discovering it as a
 * `[object Object]` on a partner's screen is not.
 */
export function localize(value, locale, defaultLocale = DEFAULT_LOCALE) {
    if (value === null || value === undefined)
        return '';
    if (typeof value === 'string')
        return value;
    const usable = (s) => typeof s === 'string' && s.length > 0;
    return (pick(value, locale, usable) ??
        pick(value, defaultLocale, usable) ??
        anyValue(value, usable) ??
        '');
}
/** `localize` for the list-valued fields. An absent list is `[]`, never `null`. */
export function localizeList(value, locale, defaultLocale = DEFAULT_LOCALE) {
    if (value === null || value === undefined)
        return [];
    if (Array.isArray(value))
        return [...value];
    const map = value;
    const usable = (v) => Array.isArray(v) && v.length > 0;
    const found = pick(map, locale, usable) ?? pick(map, defaultLocale, usable) ?? anyValue(map, usable);
    return found === undefined ? [] : [...found];
}
/* ------------------------------------------------------------------ */
/* Writing                                                             */
/* ------------------------------------------------------------------ */
/**
 * The write-side mirror: a plain string becomes a one-entry map keyed by the
 * locale it was written in, and a map passes through.
 *
 * Every write path in the system goes through this — the operator CLI's
 * `seed-catalog`, the staff app's service editor, the LoveSpa importer, the
 * demo-fleet seeder — so "a tenant that has never heard of locales writes
 * `{"en": "…"}` and reads back `"…"`" is one function rather than a convention.
 * An empty string writes an **empty map**, not `{"en": ""}`: absence is absence.
 */
export function toLocalized(value, locale = DEFAULT_LOCALE) {
    if (value === null || value === undefined)
        return {};
    if (typeof value === 'string')
        return value.length === 0 ? {} : { [canonical(locale)]: value };
    const out = {};
    for (const [key, text] of Object.entries(value)) {
        if (typeof text === 'string' && text.length > 0)
            out[canonical(key)] = text;
    }
    return out;
}
/** `toLocalized` for the list-valued fields. An empty list writes an empty map. */
export function toLocalizedList(value, locale = DEFAULT_LOCALE) {
    if (value === null || value === undefined)
        return {};
    if (Array.isArray(value))
        return value.length === 0 ? {} : { [canonical(locale)]: [...value] };
    const out = {};
    for (const [key, list] of Object.entries(value)) {
        if (Array.isArray(list) && list.length > 0)
            out[canonical(key)] = [...list];
    }
    return out;
}
/* ------------------------------------------------------------------ */
/* Negotiation                                                         */
/* ------------------------------------------------------------------ */
/**
 * Parse an `Accept-Language` header into tags, most-wanted first.
 *
 * `*` is dropped (it means "anything", which is what the fallback already does)
 * and malformed entries are skipped rather than raising: a header is attacker-
 * controlled input on a public endpoint, and the correct response to a broken
 * one is the tenant's default language, not a `400`.
 */
export function parseAcceptLanguage(header) {
    if (header === null || header === undefined)
        return [];
    const entries = [];
    header.split(',').forEach((part, order) => {
        const [rawTag, ...params] = part.trim().split(';');
        const tag = (rawTag ?? '').trim();
        if (tag === '' || tag === '*')
            return;
        if (!Locale.safeParse(tag).success)
            return;
        let q = 1;
        for (const param of params) {
            const m = /^\s*q\s*=\s*([01](?:\.\d{1,3})?)\s*$/.exec(param);
            if (m !== null)
                q = Number(m[1]);
        }
        if (q <= 0)
            return;
        entries.push({ tag, q, order });
    });
    // Stable by header order within equal q, so a header that lists two languages
    // at the same weight is answered the way it was written.
    entries.sort((a, b) => b.q - a.q || a.order - b.order);
    return entries.map((e) => e.tag);
}
/**
 * Pick the locale to answer in.
 *
 * `preferences` is the caller's order of want — the parsed `Accept-Language`,
 * then the stored `clients.locale`, then whatever else the surface knows.
 * `supported` is the tenant's declared `supported_locales`.
 *
 * A tenant that has declared **nothing** supported gets the first preference
 * passed through rather than the default: `localize` is the thing that decides
 * what actually exists, and pre-empting it here would mean a tenant who
 * translated their menu but forgot the configuration row serves English forever.
 * The configuration narrows; the data decides.
 */
export function negotiateLocale(preferences, supported, fallback = DEFAULT_LOCALE) {
    const wanted = preferences.filter((p) => typeof p === 'string' && p.trim() !== '');
    if (supported.length === 0)
        return wanted[0] ?? fallback;
    for (const want of wanted) {
        const match = localeCandidates(supported, want)[0];
        if (match !== undefined)
            return match;
    }
    return localeCandidates(supported, fallback)[0] ?? supported[0] ?? fallback;
}
//# sourceMappingURL=i18n.js.map